import React from 'react';
import useGTM from '@elgorditosalsero/react-gtm-hook';

import { DEVELOPMENT_GTM_ID, PRODUCTION_GTM_ID } from './Analytics.constant';

type EricssonGTMArg = Readonly<{
  event: 'demoEventAiOperation';
  demoEventAction: 'Discover More' | 'Viewed Content' | 'Contact us';
  demoEventLabel: string;
}>;

type CaseCategory =
  | 'Ensure superior network performance'
  | 'Create future-proof customer experience'
  | 'Turn operations into an efficient business enabler';

type CaseTab = 'Overview' | 'Challenges' | 'Benefits';

/**
 * Hook for communicating with the Ericsson GTM container
 */
export const useAnalytics = () => {
  const { init, sendDataToGTM } = useGTM();

  /**
   * Should be called at app startup to initialize the GTM container
   */
  const InitCallback = React.useCallback(() => {
    const gtmId = process.env.NODE_ENV === 'development' ? DEVELOPMENT_GTM_ID : PRODUCTION_GTM_ID;

    init({ id: gtmId, dataLayer: {} });
  }, [init]);

  /**
   * Should be called when the 'Discover more' button clicked by the user on the Home screen
   * on the back of the flipping cards
   */
  const DiscoverMoreClickCallback = React.useCallback(
    (category: CaseCategory) => {
      const eventArgs: EricssonGTMArg = {
        event: 'demoEventAiOperation',
        demoEventAction: 'Discover More',
        demoEventLabel: category,
      };

      sendDataToGTM(eventArgs);
    },
    [sendDataToGTM]
  );

  /**
   * Should be called when the the user views a tab content on the a case screen
   */
  const ContentViewCallback = React.useCallback(
    (category: CaseCategory, tab: CaseTab) => {
      const eventArgs: EricssonGTMArg = {
        event: 'demoEventAiOperation',
        demoEventAction: 'Viewed Content',
        demoEventLabel: `${category}, ${tab}`,
      };

      sendDataToGTM(eventArgs);
    },
    [sendDataToGTM]
  );

  /**
   * Should be called when the 'Contact us' button clicked by the user on a Case screen
   * on a Case tab
   */
  const ContactUsClickCallback = React.useCallback(
    (category: CaseCategory, tab: CaseTab) => {
      const eventArgs: EricssonGTMArg = {
        event: 'demoEventAiOperation',
        demoEventAction: 'Contact us',
        demoEventLabel: `${category}, ${tab}`,
      };

      sendDataToGTM(eventArgs);
    },
    [sendDataToGTM]
  );

  return {
    init: InitCallback,
    onDiscoverMoreClick: DiscoverMoreClickCallback,
    onContentView: ContentViewCallback,
    onContactUsClick: ContactUsClickCallback,
  };
};
